import React, { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";

/** Seconds elapsed while `active` is true; resets to 0 when it goes false. */
export function useElapsed(active) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!active) { setSecs(0); return undefined; }
    setSecs(0);
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
  return secs;
}


/** Natural comparator (foo2.pdf before foo10.pdf). */
export const naturalCompare = (a, b) =>
  (a || "").localeCompare(b || "", undefined, { numeric: true, sensitivity: "base" });

/** Uppercase file extension, e.g. "PDF". Empty string when none. */
export const fileExt = (name) => {
  const m = /\.([a-z0-9]+)$/i.exec(name || "");
  return m ? m[1].toUpperCase() : "";
};

const DEFAULT_ACCESSORS = {
  title: (d) => d.title || d.file_name || "",
  file_name: (d) => d.file_name || "",
  type: (d) => fileExt(d.file_name),
  file_size: (d) => d.file_size || 0,
  created_at: (d) => new Date(d.created_at || 0).getTime(),
};

/** Return a new, sorted array of documents. */
export function sortDocuments(docs, sortKey, sortDir, accessors = {}) {
  const acc = { ...DEFAULT_ACCESSORS, ...accessors };
  const getVal = acc[sortKey] || acc.title;
  const numeric = sortKey === "file_size" || sortKey === "created_at";
  const sorted = [...docs].sort((a, b) => {
    const va = getVal(a);
    const vb = getVal(b);
    const cmp = numeric ? (va || 0) - (vb || 0) : naturalCompare(String(va), String(vb));
    return sortDir === "asc" ? cmp : -cmp;
  });
  return sorted;
}

/**
 * Windows-explorer style selection given a click event.
 * Returns { selected: Set, anchor: id }.
 */
export function selectionAfterClick(prev, anchorId, id, e, orderedIds) {
  const isCtrl = e.ctrlKey || e.metaKey;
  const isShift = e.shiftKey;
  if (isShift && anchorId) {
    const a = orderedIds.indexOf(anchorId);
    const b = orderedIds.indexOf(id);
    if (a !== -1 && b !== -1) {
      const [lo, hi] = a < b ? [a, b] : [b, a];
      return { selected: new Set(orderedIds.slice(lo, hi + 1)), anchor: anchorId };
    }
  }
  if (isCtrl) {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return { selected: n, anchor: id };
  }
  return { selected: new Set([id]), anchor: id };
}

/** Sortable table header cell. */
export function SortHeader({ label, colKey, sortKey, sortDir, onSort, align = "left" }) {
  const active = sortKey === colKey;
  return (
    <th
      className={`px-4 py-2.5 text-${align} label text-[color:var(--atech-text-muted)] font-normal whitespace-nowrap select-none`}
      data-testid={`sort-header-${colKey}`}
    >
      <button
        type="button"
        onClick={() => onSort(colKey)}
        className={`inline-flex items-center gap-1 transition-colors hover:text-[color:var(--atech-text)] ${active ? "text-[color:var(--atech-copper)]" : ""}`}
      >
        {label}
        {active ? (
          sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />
        ) : (
          <ChevronsUpDown size={11} className="opacity-30" />
        )}
      </button>
    </th>
  );
}
